import {useGetInfiniteBasicDataItems} from '@/modules/basic-data/api'
import {useGetInfiniteSections} from '@/modules/sections/api'
import {useGetInfiniteDoctors} from '@/modules/users/api'
import {useGetInfiniteWards} from '@/modules/wards/api'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Button, ButtonGroup, useDialogContext, VStack} from '@chakra-ui/react'
import {queries} from '@cypro/engine/queries'
import {BasicDataResources} from '@cypro/engine/schema'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {useQuery} from '@rocicorp/zero/react'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import * as z from 'zod'

type PatientAdminCare = {
  sectionId?: string | null
  wardId?: string | null
  customerId?: string | null
  ambulatory: boolean | null
}

const AdminCareSchema = z.object({
  sectionId: z.uuid().nullable(),
  wardId: z.uuid().nullable(),
  familyDoctorId: z.uuid().nullable(),
  responsibleDoctorId: z.uuid().nullable(),
  customerId: z.uuid().nullable(),
  ambulatory: z.boolean(),
})

export function AdminCareDialog({
  patientId,
  responsibleDoctorId,
  familyDoctorId,
  adminDetails,
}: {
  patientId: string
  responsibleDoctorId: string | null
  familyDoctorId: string | null
  adminDetails?: PatientAdminCare
}) {
  const {t} = useTranslate('common')
  const zero = useZero()
  const context = useDialogContext()

  const infiniteSections = useGetInfiniteSections()
  const infiniteResponsibleDoctors = useGetInfiniteDoctors()
  const infiniteFamilyDoctors = useGetInfiniteBasicDataItems({
    resource: BasicDataResources.generalPhysicians,
    sort: {field: 'name', dir: 'asc'},
  })

  const [customers] = useQuery(queries.customers.listCustomers({sort: {field: 'name', dir: 'asc'}}))
  const customerItems = getOptionsList(customers, 'name', 'id')

  const form = useAppForm({
    defaultValues: {
      familyDoctorId: familyDoctorId || null,
      sectionId: adminDetails?.sectionId || null,
      wardId: adminDetails?.wardId || null,
      responsibleDoctorId: responsibleDoctorId || null,
      customerId: adminDetails?.customerId || null,
      ambulatory: Boolean(adminDetails?.ambulatory),
    },
    validators: {
      onSubmit: AdminCareSchema,
    },
    onSubmit({value}) {
      const {sectionId, wardId, familyDoctorId, responsibleDoctorId, customerId, ambulatory} = value

      return zero.attemptMutation(
        zero.mutators.patients.updateEncounter({
          patientId,
          sectionId,
          wardId,
          familyDoctorId,
          responsibleDoctorId,
          customerId,
          ambulatory,
        }),
        async mutation => {
          const data = await mutation.server
          if (!data) return
          toaster.success({title: t('success'), description: t('patient_updated_successfully')})
          onClose()
        }
      )
    },
  })

  function onClose() {
    context.setOpen(false)
  }

  const isDefaultValue = useStore(form.store, state => state.isDefaultValue)

  // a ward belongs to a section, so only offer the selected section's wards (all of them without one)
  const sectionId = useStore(form.store, state => state.values.sectionId)
  const infiniteWards = useGetInfiniteWards({filters: sectionId ? {sectionId} : undefined})
  const wardId = useStore(form.store, state => state.values.wardId)
  const [selectedWard] = useQuery(queries.locations.getWardById(wardId ?? ''))

  function onSectionChange(newSectionId: string | null) {
    if (newSectionId && selectedWard && selectedWard.sectionId !== newSectionId) {
      form.setFieldValue('wardId', null)
    }
  }

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <DialogHeader>{t('patient.encounters')}</DialogHeader>

        <DialogBody minHeight="none">
          <VStack gap="5" width="100%">
            <form.AppField name="responsibleDoctorId" mode="value">
              {field => (
                <field.InfiniteCombobox
                  label={t('responsible_doctor')}
                  placeholder={t('eg', {value: t('name')})}
                  contentProps={{zIndex: 2000}}
                  {...infiniteResponsibleDoctors}
                />
              )}
            </form.AppField>
            <form.AppField name="familyDoctorId" mode="value">
              {field => (
                <field.InfiniteCombobox
                  label={t('family_doctor')}
                  placeholder={t('eg', {value: t('name')})}
                  contentProps={{zIndex: 2000}}
                  {...infiniteFamilyDoctors}
                />
              )}
            </form.AppField>
            <form.AppField
              name="sectionId"
              mode="value"
              listeners={{onChange: ({value}) => onSectionChange(value)}}
            >
              {field => (
                <field.InfiniteCombobox
                  label={t('section')}
                  placeholder={t('eg', {value: 'A103b'})}
                  contentProps={{zIndex: 2000}}
                  {...infiniteSections}
                />
              )}
            </form.AppField>
            <form.AppField name="wardId" mode="value">
              {field => (
                <field.InfiniteCombobox
                  label={t('ward')}
                  placeholder={t('eg', {value: 'Station 3'})}
                  contentProps={{zIndex: 2000}}
                  {...infiniteWards}
                />
              )}
            </form.AppField>
            <form.AppField name="customerId" mode="value">
              {field => (
                <field.Select
                  label={t('customer_of')}
                  placeholder={t('eg', {value: customerItems?.[0]?.label})}
                  items={customerItems}
                  contentProps={{zIndex: 2000}}
                />
              )}
            </form.AppField>
            <form.AppField name="ambulatory">{field => <field.Switch label={t('ambulatory')} />}</form.AppField>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={onClose}>
              {t('cancel')}
            </Button>
            <form.SubmitButton disabled={isDefaultValue}>{t('save')}</form.SubmitButton>
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}

function getOptionsList<T extends Record<string, unknown>>(
  list: T[],
  labelKey: keyof T,
  valueKey: keyof T
): Array<{label: string; value: string}> {
  return list.map(item => ({
    label: String(item[labelKey] ?? item[valueKey] ?? ''),
    value: String(item[valueKey] ?? ''),
  }))
}
